import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Github, Twitter, Linkedin, MapPin, Map } from 'lucide-react';
import Logo from './devNectar_new_logo.png';
import Swal from 'sweetalert2';

const Footer: React.FC = () => {
  const [activeIcon, setActiveIcon] = React.useState<string | null>(null);

  const handleIconClick = (e: React.MouseEvent, iconId: string) => {
    e.preventDefault();
    setActiveIcon(iconId);
    
    Swal.fire({
      icon: 'info',
      title: 'Greetings! 👋',
      text: `Our ${iconId.charAt(0).toUpperCase() + iconId.slice(1)} integration is coming soon. Stay tuned!`,
      confirmButtonColor: '#3b82f6',
      width: 360,
      padding: '1.5em',
      customClass: {
        popup: 'rounded-2xl',
        icon: '!scale-75 !mt-2 !mb-2',
        title: '!text-xl !mt-0',
        confirmButton: 'rounded-xl px-8 py-2 font-semibold focus:ring-2 focus:ring-blue-500'
      }
    });

    setTimeout(() => {
      setActiveIcon(null);
    }, 400); // matches animation duration
  };

  return (
    <footer data-id="main-footer" className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div data-id="footer-company" className="space-y-4">
            <div className="flex items-center space-x-2">
              <Link to="/" data-id="footer-logo">
                <img src={Logo} alt="logo" className="h-14 w-auto object-contain" loading="lazy" />
              </Link>
            </div>
            <p className="text-gray-400 text-sm">
              Professional software development solutions for all technologies. We turn your ideas into reality with cutting-edge technology and expert support.
            </p>
            <div className="flex space-x-4">
              <a 
                href="#!" 
                data-id="footer-github" 
                onClick={(e) => handleIconClick(e, 'github')}
                className={`text-gray-400 footer-icon-dynamic ${activeIcon === 'github' ? 'animate-slide' : ''}`}
              >
                <Github className="h-6 w-6" />
              </a>
              <a 
                href="#!" 
                data-id="footer-twitter" 
                onClick={(e) => handleIconClick(e, 'twitter')}
                className={`text-gray-400 footer-icon-dynamic ${activeIcon === 'twitter' ? 'animate-slide' : ''}`}
              >
                <Twitter className="h-6 w-6" />
              </a>
              <a 
                href="#!" 
                data-id="footer-linkedin" 
                onClick={(e) => handleIconClick(e, 'linkedin')}
                className={`text-gray-400 footer-icon-dynamic ${activeIcon === 'linkedin' ? 'animate-slide' : ''}`}
              >
                <Linkedin className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div data-id="footer-links" className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" data-id="footer-link-home" className="footer-link-dynamic text-gray-400 hover:text-white transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" data-id="footer-link-services" className="footer-link-dynamic text-gray-400 hover:text-white transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/portfolio" data-id="footer-link-portfolio" className="footer-link-dynamic text-gray-400 hover:text-white transition-colors text-sm">
                  Portfolio
                </Link>
              </li>
              {/* <li>
                <Link to="/careers" data-id="footer-link-careers" className="footer-link-dynamic text-gray-400 hover:text-white transition-colors text-sm">
                  Careers
                </Link>
              </li> */}
              <li>
                <Link to="/contact" data-id="footer-link-contact" className="footer-link-dynamic text-gray-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div data-id="footer-services" className="space-y-4">
            <h3 className="text-lg font-semibold">Services</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <Link to="/services" data-id="footer-service-web" className="footer-link-dynamic inline-block hover:text-white transition-colors">
                  Web Development
                </Link>
              </li>
              <li>
                <Link to="/services" data-id="footer-service-mobile" className="footer-link-dynamic inline-block hover:text-white transition-colors">
                  Mobile Development
                </Link>
              </li>
              <li>
                <Link to="/services" data-id="footer-service-ai" className="footer-link-dynamic inline-block hover:text-white transition-colors">
                  AI/ML Solutions
                </Link>
              </li>
              <li>
                <Link to="/services" data-id="footer-service-cloud" className="footer-link-dynamic inline-block hover:text-white transition-colors">
                  Cloud Services
                </Link>
              </li>
              <li>
                <Link to="/services" data-id="footer-service-consulting" className="footer-link-dynamic inline-block hover:text-white transition-colors">
                  Technical Consulting
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div data-id="footer-contact" className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Info</h3>
            <div className="space-y-3 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span data-id="footer-email">sales@devNectar.in</span>
              </div>
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 mt-1 flex-shrink-0" />
                <div>
                  <span data-id="footer-address" className="block text-gray-400">
                    Office No. 827, 8th Floor, Suratwala Mark Plazzo, Hinjewadi Phase-1, Pune - 411057
                  </span>
                  <a
                    href="https://maps.app.goo.gl/K1tg2ySdGbg7QHtJA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Map className="h-3 w-3 mr-1" />
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-6 pt-4 text-center">
          <p data-id="footer-copyright" className="text-gray-400 text-sm">
            © 2025 devNectar. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;